'use client';

import * as React from 'react';

import { classifyNetworkError } from '../../infrastructure/api/network/classifyNetworkError';
import { getNetworkErrorMessage } from '../../infrastructure/api/network/errorMessages';
import { HttpError } from '../../utilities/errors/HTTPErrors';
import { isTransientError } from '../../utilities/errors/isTransientError';

import { UserFriendlyError } from './UserFriendlyError';

export type ConnectionErrorFallbackProps = {
  error: unknown;
  onRetry?: () => void;
  isRetrying?: boolean;
  title?: string;
};

function titleForNetworkError(error: unknown): string {
  const networkType = classifyNetworkError(error);
  switch (networkType) {
    case 'offline':
      return 'You are offline';
    case 'timeout':
      return 'Request timed out';
    case 'server_unreachable':
      return 'Connection lost';
    case 'server_error':
      return 'Server unavailable';
    default:
      return 'Connection problem';
  }
}

function messageForError(error: unknown): string {
  if (error instanceof HttpError && error.message.length > 0) {
    return error.message;
  }
  return getNetworkErrorMessage(classifyNetworkError(error));
}

/**
 * Request-level fallback for transient network failures with manual retry.
 */
export function ConnectionErrorFallback({
  error,
  onRetry,
  isRetrying = false,
  title,
}: Readonly<ConnectionErrorFallbackProps>): React.ReactElement | null {
  if (!isTransientError(error)) {
    return null;
  }

  return (
    <UserFriendlyError
      title={title ?? titleForNetworkError(error)}
      message={messageForError(error)}
      severity="warning"
      alertTitle="Connection issue"
      retryLabel={isRetrying ? 'Retrying…' : 'Tap to retry'}
      onRetry={isRetrying ? undefined : onRetry}
    />
  );
}
